import { z } from 'zod';

// Identity only. A stored record is a navigation hint, never an access grant.
// Real activities use UUIDs; bounded synthetic IDs also support local previews.
const identifier = z
  .string()
  .max(160)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*$/);
export const studioVisitInputSchema = z.object({
  programSlug: identifier,
  activityId: identifier,
  kind: z.enum(['lesson', 'workshop', 'checkpoint']),
});
const storedVisitSchema = studioVisitInputSchema.extend({
  visitedAt: z.iso.datetime(),
});

export type StudioVisitInput = z.infer<typeof studioVisitInputSchema>;
export type StudioVisit = z.infer<typeof storedVisitSchema>;

export function studioResumeKey(userId: string) {
  return `mio.studio.resume.v1:${encodeURIComponent(userId)}`;
}

export function parseStudioVisit(value: string | null): StudioVisit | null {
  if (!value || value.length > 2048) return null;
  try {
    const parsed = storedVisitSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function readStudioVisit(userId: string | null): StudioVisit | null {
  if (!userId) return null;
  try {
    return parseStudioVisit(localStorage.getItem(studioResumeKey(userId)));
  } catch {
    return null;
  }
}

export function saveStudioVisit(userId: string, visit: StudioVisit) {
  try {
    localStorage.setItem(studioResumeKey(userId), JSON.stringify(visit));
  } catch {
    // The provider retains the visit in memory when storage is blocked or full.
  }
}
